import { BEEF_AI_ACCESS_TOKEN, beefAiGet } from "../common/utils/beefAiClient";
import { getCountFromBeefAiAggregateRow } from "../routes/pricing-reports/productSnapshotChartUtils";
import {
  applyPriceHistoryDimensionFilters,
  applyPriceHistoryQualityFilters,
  fetchDistinctCounts,
  fetchProductsByIds,
  normalizePriceHistoryBreakdownRow,
} from "./beefAiPricing";

export const FLYER_REPORT_PROVIDERS_INCLUDE = ["workflow:retail-flyer-market-analysis"];

const FLYER_GALLERY_PAGE_FIELD_NAMES = [
  "id",
  "record_at",
  "date_created",
  "product_id",
  "product_name",
  "raw_product_name",
  "retailer",
  "province",
  "provider",
  "price",
  "unit_price",
  "unit",
  "unit_quantity",
  "sale_type",
  "sale_amount",
  "original_price",
  "is_discounted",
  "images",
  "human_verified",
  "revision_status",
  "revision_metadata",
];

/**
 * @typedef {object} FlyerReportQueryFilters
 * @property {string} [dateFrom]
 * @property {string} [dateTo]
 * @property {string} [province]
 * @property {string} [retailer]
 * @property {string} [productId]
 * @property {'weight'} [unitType]
 * @property {string[]} [providersInclude]
 */

/**
 * @typedef {object} FlyerReportSummaryStats
 * @property {number} totalOfferCount
 * @property {number} uniqueRetailerCount
 * @property {{ productId: string, productName: string, offerCount: number } | null} topProduct
 */

function accessToken() {
  return BEEF_AI_ACCESS_TOKEN;
}

/**
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 */
export function toPricingFiltersFromFlyerReportQuery(flyerReportQueryFilters = {}) {
  return {
    dateFrom: flyerReportQueryFilters.dateFrom,
    dateTo: flyerReportQueryFilters.dateTo,
    province: flyerReportQueryFilters.province,
    retailer: flyerReportQueryFilters.retailer,
    productId: flyerReportQueryFilters.productId,
    unitType: flyerReportQueryFilters.unitType || "weight",
    providersInclude: FLYER_REPORT_PROVIDERS_INCLUDE,
  };
}

/**
 * @param {URLSearchParams} params
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 */
export function applyFlyerReportBaseFilters(params, flyerReportQueryFilters = {}) {
  const pricingFilters = toPricingFiltersFromFlyerReportQuery(flyerReportQueryFilters);
  applyPriceHistoryQualityFilters(params, pricingFilters);
  applyPriceHistoryDimensionFilters(params, pricingFilters, { includeDate: true });
}

/**
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 * @param {object} groupByOptions
 * @param {string[]} [groupByOptions.groupBy]
 * @param {string} [groupByOptions.sort]
 * @param {boolean} [groupByOptions.aggregateCount]
 * @param {string | null} [groupByOptions.aggregateAvgField]
 * @param {string | null} [groupByOptions.saleTypeEq]
 */
export function buildFlyerAggregateParams(flyerReportQueryFilters = {}, groupByOptions = {}) {
  const {
    groupBy = [],
    sort = "",
    aggregateCount = true,
    aggregateAvgField = null,
    saleTypeEq = null,
  } = groupByOptions;

  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  if (aggregateCount) params.set("aggregate[count]", "*");
  if (aggregateAvgField) params.set("aggregate[avg]", aggregateAvgField);
  groupBy.forEach((key) => params.append("groupBy[]", key));
  params.set("limit", "-1");
  if (sort) params.set("sort", sort);
  if (saleTypeEq) params.set("filter[sale_type][_eq]", saleTypeEq);
  applyFlyerReportBaseFilters(params, flyerReportQueryFilters);
  return params;
}

/**
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 * @param {object} groupByOptions
 * @returns {Promise<object[]>}
 */
async function fetchFlyerAggregateRows(flyerReportQueryFilters, groupByOptions) {
  const params = buildFlyerAggregateParams(flyerReportQueryFilters, groupByOptions);
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

function buildDateOnlyFlyerFilters({ dateFrom, dateTo }) {
  return {
    dateFrom,
    dateTo,
    unitType: "weight",
    providersInclude: FLYER_REPORT_PROVIDERS_INCLUDE,
  };
}

/**
 * @param {object[]} retailerGroupRows
 */
function countUniqueRetailersFromGroupRows(retailerGroupRows = []) {
  return retailerGroupRows.filter((row) => String(row?.retailer ?? "").trim()).length;
}

/**
 * @param {object[]} topProductRows
 */
async function resolveTopProductFromAggregateRows(topProductRows = []) {
  const candidates = (topProductRows || [])
    .map((row) => {
      const productId = String(row.product_id ?? "").trim();
      if (!productId) return null;
      return {
        productId,
        productName: String(row.product_name ?? "").trim(),
        offerCount: getCountFromBeefAiAggregateRow(row),
      };
    })
    .filter(Boolean);

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    if (b.offerCount !== a.offerCount) return b.offerCount - a.offerCount;
    const nameA = a.productName || a.productId;
    const nameB = b.productName || b.productId;
    const byName = nameA.localeCompare(nameB);
    if (byName !== 0) return byName;
    return a.productId.localeCompare(b.productId);
  });

  const top = candidates[0];
  let productName = top.productName || top.productId;

  try {
    const catalogRows = await fetchProductsByIds([top.productId]);
    const catalogName = catalogRows[0]?.name;
    if (catalogName) productName = catalogName;
  } catch (_) {
    // Fallback to aggregate / id already set.
  }

  return {
    productId: top.productId,
    productName,
    offerCount: top.offerCount,
  };
}

/**
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 * @returns {Promise<FlyerReportSummaryStats>}
 */
export async function fetchFlyerReportSummaryStats(flyerReportQueryFilters) {
  const [totalCountRows, retailerGroupRows, topProductRows] = await Promise.all([
    fetchFlyerAggregateRows(flyerReportQueryFilters, {
      groupBy: [],
      aggregateCount: true,
    }),
    fetchFlyerAggregateRows(flyerReportQueryFilters, {
      groupBy: ["retailer"],
      sort: "retailer",
    }),
    fetchFlyerAggregateRows(flyerReportQueryFilters, {
      groupBy: ["product_id"],
      sort: "product_id",
    }),
  ]);

  const totalOfferCount = getCountFromBeefAiAggregateRow(totalCountRows[0] || {});
  const uniqueRetailerCount = countUniqueRetailersFromGroupRows(retailerGroupRows);

  if (totalOfferCount === 0) {
    return {
      totalOfferCount: 0,
      uniqueRetailerCount: 0,
      topProduct: null,
    };
  }

  const topProduct = await resolveTopProductFromAggregateRows(topProductRows);

  return {
    totalOfferCount,
    uniqueRetailerCount,
    topProduct,
  };
}

/**
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 * @returns {Promise<object[]>}
 */
export async function fetchFlyerOfferCountByRetailerAndDiscountType(flyerReportQueryFilters) {
  return fetchFlyerAggregateRows(flyerReportQueryFilters, {
    groupBy: ["retailer", "sale_type"],
    sort: "retailer",
  });
}

/**
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 * @returns {Promise<object[]>}
 */
export async function fetchFlyerPromotionalFrequency(flyerReportQueryFilters) {
  return fetchFlyerAggregateRows(flyerReportQueryFilters, {
    groupBy: ["year(record_at)", "month(record_at)"],
    sort: "year(record_at),month(record_at)",
  });
}

/**
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 * @returns {Promise<object[]>}
 */
export async function fetchFlyerOfferCountByProvince(flyerReportQueryFilters) {
  return fetchFlyerAggregateRows(flyerReportQueryFilters, {
    groupBy: ["province"],
    sort: "province",
  });
}

/**
 * @param {{ dateFrom?: string, dateTo?: string }} options
 * @returns {Promise<{ provinces: object[], retailers: object[], products: object[] }>}
 */
export async function fetchFlyerReportDistinctFilterOptions({ dateFrom, dateTo }) {
  const dateOnlyFlyerFilters = buildDateOnlyFlyerFilters({ dateFrom, dateTo });

  const [provinces, retailers, productRows] = await Promise.all([
    fetchDistinctCounts("province", dateOnlyFlyerFilters),
    fetchDistinctCounts("retailer", dateOnlyFlyerFilters),
    fetchFlyerAggregateRows(dateOnlyFlyerFilters, {
      groupBy: ["product_id"],
      sort: "product_id",
    }),
  ]);

  return {
    provinces,
    retailers,
    products: productRows,
  };
}

/**
 * @param {URLSearchParams} params
 */
function appendFlyerGalleryPageFieldParams(params) {
  FLYER_GALLERY_PAGE_FIELD_NAMES.forEach((fieldName) => {
    params.append("fields[]", fieldName);
  });
}

/**
 * Paginated promotional `price_history` rows for the flyer gallery browse grid.
 *
 * @param {FlyerReportQueryFilters} flyerReportQueryFilters
 * @param {{ page?: number, limit?: number }} [pagination]
 * `page` is 1-based (Directus). Defaults: page 1, limit 10.
 * @returns {Promise<{ rows: object[], totalCount: number }>}
 */
export async function fetchFlyerGalleryPage(flyerReportQueryFilters, pagination = {}) {
  const limit = pagination.limit != null ? pagination.limit : 10;
  const page = pagination.page != null ? pagination.page : 1;

  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  params.set("limit", String(limit));
  params.set("page", String(page));
  params.append("meta[]", "filter_count");
  params.set("sort", "-record_at,-date_created");
  appendFlyerGalleryPageFieldParams(params);
  applyFlyerReportBaseFilters(params, flyerReportQueryFilters);

  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  const rawRows = json.data || [];
  const filterCountFromMeta = typeof json.meta?.filter_count === "number";
  const totalCount = filterCountFromMeta
    ? json.meta.filter_count
    : (page - 1) * limit + rawRows.length;

  return {
    rows: rawRows.map(normalizePriceHistoryBreakdownRow),
    totalCount,
  };
}
